import React from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import SkipNextRoundedIcon from '@mui/icons-material/SkipNextRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';

/**
 * Timer control buttons — Start/Pause/Resume, Skip, Reset.
 *
 * Props:
 *  status: 'IDLE' | 'RUNNING' | 'PAUSED'
 *  onStart, onPause, onResume, onSkip, onReset: () => void
 */
const TimerControls = ({ status, onStart, onPause, onResume, onSkip, onReset }) => {
  const isRunning = status === 'RUNNING';
  const isPaused = status === 'PAUSED';
  const isIdle = status === 'IDLE';

  const handlePrimary = () => {
    if (isIdle) onStart();
    else if (isRunning) onPause();
    else if (isPaused) onResume();
  };

  const primaryIcon = isRunning ? <PauseRoundedIcon sx={{ fontSize: 36 }} /> : <PlayArrowRoundedIcon sx={{ fontSize: 36 }} />;
  const primaryLabel = isRunning ? 'Pause' : isPaused ? 'Resume' : 'Start';
  const primaryColor = isRunning ? '#f59e0b' : '#4ade80';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        justifyContent: 'center',
        mt: 2,
      }}
    >
      {/* Reset */}
      <Tooltip title="Reset timer" placement="top" arrow>
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            onClick={onReset}
            sx={{
              color: 'rgba(255,255,255,0.4)',
              '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
              transition: 'color 0.2s, background-color 0.2s',
            }}
          >
            <RestartAltRoundedIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </span>
      </Tooltip>

      {/* Primary action — Start / Pause / Resume */}
      <Box
        onClick={handlePrimary}
        sx={{
          width: 72,
          height: 72,
          borderRadius: '50%',
          backgroundColor: primaryColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: `0 0 24px ${primaryColor}66`,
          transition: 'all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          '&:hover': {
            transform: 'scale(1.08)',
            boxShadow: `0 0 36px ${primaryColor}88`,
          },
          '&:active': { transform: 'scale(0.96)' },
          color: '#111',
        }}
        role="button"
        aria-label={primaryLabel}
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handlePrimary()}
      >
        {primaryIcon}
      </Box>

      {/* Skip */}
      <Tooltip title="Skip to next phase" placement="top" arrow>
        <span style={{ display: 'inline-flex' }}>
          <IconButton
            onClick={onSkip}
            sx={{
              color: 'rgba(255,255,255,0.4)',
              '&:hover': { color: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
              transition: 'color 0.2s, background-color 0.2s',
            }}
          >
            <SkipNextRoundedIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export default TimerControls;
